import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { axiosClient } from "@/lib/axiosClient";
import { Button } from "@/components/ui/button";
import confetti from "canvas-confetti";
import axios from "axios";
import { useTranslation } from "react-i18next"; // 1. Import hook i18n

export default function RegisterSuccessPage() {
    const { t } = useTranslation('registration'); // 2. Gunakan namespace registration
    const [searchParams] = useSearchParams();
    const email = searchParams.get("email") || "";
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [progress, setProgress] = useState(0);

    // 🎉 Efek confetti ketika halaman dibuka
    useEffect(() => {
        if (!email) return;
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
        });
    }, [email]);

    // 🔁 Fungsi kirim ulang link aktivasi
    const resendActivation = async () => {
        if (!email) {
            setMessage(t('success_page.status.email_not_found'));
            return;
        }

        setLoading(true);
        setMessage("");
        setProgress(0);

        const interval = setInterval(() => {
            setProgress((old) => (old < 90 ? old + 10 : old));
        }, 200);

        try {
            const res = await axiosClient.post("/resend-activation", { email });
            setMessage(res.data?.message ?? t('success_page.status.resend_success'));
            setProgress(100);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                setMessage(
                    error.response?.data?.message ??
                    t('success_page.status.resend_error')
                );
            } else {
                setMessage(t('success_page.status.unexpected_error'));
            }
            setProgress(100);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setLoading(false);
                setProgress(0);
            }, 600);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-100 px-4">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center"
            >
                {/* 🖼️ Logo Konaspi XII */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="flex flex-col items-center mb-6"
                >
                    <img
                        src="/konaspi.png"
                        alt="Logo Konaspi XII"
                        className="w-24 h-auto mb-3"
                    />
                    {/* Garis pemisah di bawah logo */}
                    <div className="w-40 h-[2px] bg-gradient-to-r from-indigo-400 via-purple-500 to-blue-500 rounded-full"></div>
                </motion.div>

                <h1 className="text-3xl font-bold text-indigo-700 mb-3">
                    {t('success_page.title')}
                </h1>

                <p className="text-gray-700 mb-6 leading-relaxed text-justify">
                    {t('success_page.thank_you')} <b>Konaspi XII</b>. <br />
                    {t('success_page.instruction_part1')}{" "}
                    <b className="text-indigo-600 break-all">{email}</b> {t('success_page.instruction_part2')}{" "}
                    <span className="font-semibold bg-gradient-to-r from-red-500 to-red-700 text-transparent bg-clip-text animate-pulse">
                        {t('success_page.activation_link_text')}
                    </span>{" "}
                    {t('success_page.instruction_part3')}
                    <br />
                    <br />
                    {t('success_page.post_activation_info')}
                </p>


                {/* Progress bar */}
                <div className="relative mb-6 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div
                        className="absolute top-0 left-0 h-full bg-indigo-500 transition-all duration-300 ease-in-out"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>

                {/* Tombol kirim ulang */}
                <Button
                    onClick={resendActivation}
                    disabled={loading || !email}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg"
                >
                    {loading ? t('success_page.btn_sending') : t('success_page.btn_resend')}
                </Button>

                {/* Pesan status */}
                {message && (
                    <p
                        className={`mt-5 text-sm ${message.toLowerCase().includes("berhasil") ||
                            message.toLowerCase().includes("success") ||
                            message.toLowerCase().includes("dikirim") ||
                            message.toLowerCase().includes("sent")
                            ? "text-green-600"
                            : "text-red-500"
                            }`}
                    >
                        {message}
                    </p>
                )}

                <div className="mt-8 text-xs text-gray-400">
                    {t('success_page.status.spam_note')}
                </div>

                {/* 🔗 Tombol ke halaman login */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.4 }}
                    className="mt-8"
                >
                    <Button
                        variant="outline"
                        onClick={() => navigate("/login")}
                        className="w-full border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                    >
                        {t('success_page.btn_login')}
                    </Button>
                </motion.div>
            </motion.div>
        </div>
    );
}